import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CalendarOff, Sparkles, Trash2, CalendarX2, CalendarCheck2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Anchored colors for common reasons + palette fallback so unknown reasons still get a color.
const REASON_COLORS = {
  school:   { chip: "bg-amber-500 text-white",   soft: "bg-amber-100 text-amber-700 hover:bg-amber-100",       dot: "bg-amber-400" },
  vacation: { chip: "bg-blue-900 text-white",   soft: "bg-sky-100 text-blue-900 hover:bg-sky-100",       dot: "bg-blue-500" },
  market:   { chip: "bg-amber-500 text-white",  soft: "bg-amber-100 text-amber-800 hover:bg-amber-100",    dot: "bg-amber-400" },
};
const FALLBACK_PALETTE = [
  { chip: "bg-purple-500 text-white",  soft: "bg-purple-100 text-purple-700 hover:bg-purple-100",  dot: "bg-purple-400" },
  { chip: "bg-sky-500 text-white",     soft: "bg-sky-100 text-sky-700 hover:bg-sky-100",           dot: "bg-sky-400" },
  { chip: "bg-rose-500 text-white",    soft: "bg-rose-100 text-rose-700 hover:bg-rose-100",        dot: "bg-rose-400" },
  { chip: "bg-emerald-500 text-white", soft: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", dot: "bg-emerald-400" },
  { chip: "bg-indigo-500 text-white",  soft: "bg-indigo-100 text-indigo-700 hover:bg-indigo-100",  dot: "bg-indigo-400" },
];
const hashStr = (s) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0);
const colorForReason = (reason) => {
  const key = (reason || "").toLowerCase().trim();
  if (!key) return { chip: "bg-stone-400 text-white", soft: "bg-stone-100 text-stone-600 hover:bg-stone-100", dot: "bg-stone-300" };
  if (REASON_COLORS[key]) return REASON_COLORS[key];
  return FALLBACK_PALETTE[hashStr(key) % FALLBACK_PALETTE.length];
};

export default function AvailabilityTab({ bookings }) {
  const [pickedDate, setPickedDate] = useState(null);
  const [reason, setReason] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkWeekdays, setBulkWeekdays] = useState([0, 1, 2, 3, 4]); // Mon-Fri
  const [bulkMonths, setBulkMonths] = useState(12);
  const [bulkReason, setBulkReason] = useState("school");
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeSaving, setRangeSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blocked-dates");
      setBlocked(data);
    } catch (e) { console.error("blocked-dates load failed", e); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const bookedDates = new Set(
    (bookings || [])
      .filter((b) => b.status !== "cancelled")
      .map((b) => b.event_date)
  );
  const blockedDates = new Set(blocked.map((b) => b.date));
  const reasonByDate = Object.fromEntries(blocked.map((b) => [b.date, b.reason || ""]));
  const uniqueReasons = [...new Set(blocked.map((b) => b.reason).filter(Boolean))];

  // Build per-reason modifier groups so each reason gets its own color in the calendar.
  const reasonModifiers = {};
  const reasonModifierClasses = {};
  uniqueReasons.forEach((r) => {
    const safe = r.replace(/[^a-z0-9]+/gi, "_").toLowerCase() || "unnamed";
    const key = `r_${safe}`;
    const dates = blocked.filter((b) => b.reason === r).map((b) => new Date(b.date + "T00:00:00"));
    reasonModifiers[key] = dates;
    reasonModifierClasses[key] = `${colorForReason(r).soft} line-through rounded-md`;
  });
  const unlabeledDates = blocked.filter((b) => !b.reason).map((b) => new Date(b.date + "T00:00:00"));

  const disabledDay = useMemo(() => (d) => {
    if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    const iso = format(d, "yyyy-MM-dd");
    return bookedDates.has(iso);
  }, [bookedDates]);

  const calendarModifiers = useMemo(() => ({
    booked: (d) => bookedDates.has(format(d, "yyyy-MM-dd")),
    unlabeled: unlabeledDates,
    ...reasonModifiers,
  }), [bookedDates, unlabeledDates, reasonModifiers]);

  const calendarModifierClasses = useMemo(() => ({
    booked: "bg-sky-100 text-blue-900 font-semibold rounded-md",
    unlabeled: "bg-stone-100 text-stone-600 line-through rounded-md",
    ...reasonModifierClasses,
  }), [reasonModifierClasses]);

  const calendarComponents = useMemo(() => ({
    DayContent: ({ date: d }) => {
      const iso = format(d, "yyyy-MM-dd");
      const reason = reasonByDate[iso];
      const title = reason
        ? `Blocked — ${reason}`
        : bookedDates.has(iso)
        ? "Booked"
        : blockedDates.has(iso)
        ? "Blocked"
        : "";
      return <span title={title}>{d.getDate()}</span>;
    },
  }), [reasonByDate, bookedDates, blockedDates]);

  const addBlocked = async () => {
    if (!pickedDate) return toast.error("Pick a date first");
    const dateStr = format(pickedDate, "yyyy-MM-dd");
    if (bookedDates.has(dateStr)) return toast.error("That date is already booked");
    setSaving(true);
    try {
      await api.post("/admin/blocked-dates", { date: dateStr, reason });
      toast.success("Date blocked");
      setReason("");
      setPickedDate(null);
      load();
    } catch (e) { console.error("failed to block date", e); toast.error("Failed to block date"); }
    setSaving(false);
  };

  const unblock = async (date) => {
    try {
      await api.delete(`/admin/blocked-dates/${date}`);
      toast.success("Date unblocked");
      load();
    } catch { toast.error("Failed to unblock"); }
  };

  const bulkBlock = async () => {
    if (bulkWeekdays.length === 0) return toast.error("Pick at least one day of the week");
    setBulkSaving(true);
    try {
      const payload = {
        weekdays: bulkWeekdays,
        months_ahead: bulkMonths,
        reason: bulkReason,
      };
      if (bulkStart) payload.start_date = bulkStart;
      if (bulkEnd) payload.end_date = bulkEnd;
      const { data } = await api.post("/admin/blocked-dates/bulk", payload);
      toast.success(`${data.added} date${data.added === 1 ? "" : "s"} blocked`);
      setBulkStart(""); setBulkEnd("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk block failed");
    }
    setBulkSaving(false);
  };

  const unblockRange = async () => {
    if (!rangeStart || !rangeEnd) return toast.error("Pick a start and end date");
    setRangeSaving(true);
    try {
      const { data } = await api.post("/admin/blocked-dates/unblock-range", {
        start_date: rangeStart,
        end_date: rangeEnd,
      });
      toast.success(`Unblocked ${data.removed} date${data.removed === 1 ? "" : "s"}`);
      setRangeStart(""); setRangeEnd("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unblock range");
    }
    setRangeSaving(false);
  };

  const bulkUnblockByReason = async (reasonMatch) => {
    const targets = blocked.filter((b) => (b.reason || "").toLowerCase() === reasonMatch.toLowerCase());
    if (!targets.length) return toast.info(`No dates blocked with reason "${reasonMatch}"`);
    if (!window.confirm(`Unblock ${targets.length} dates with reason "${reasonMatch}"?`)) return;
    try {
      await Promise.all(targets.map((t) => api.delete(`/admin/blocked-dates/${t.date}`)));
      toast.success(`Unblocked ${targets.length} dates`);
      load();
    } catch { toast.error("Some deletions failed"); }
  };

  const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const toggleWd = (i) => setBulkWeekdays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort());

  return (
    <div className="space-y-6" data-testid="admin-availability-tab">
      <Card className="border-stone-100">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Wand2 className="h-5 w-5 text-blue-900" />
            <h3 className="font-serif-display text-2xl">Bulk block</h3>
            <Button
              onClick={() => setBulkOpen((v) => !v)}
              variant="ghost"
              size="sm"
              className="ml-auto text-blue-800"
              data-testid="availability-bulk-toggle"
            >
              {bulkOpen ? "Hide" : "Show"}
            </Button>
          </div>
          <p className="text-sm text-stone-500 mb-4">Recurring commitment (like school, work, or a weekly market)? Block a whole weekday pattern in one shot.</p>
          {bulkOpen && (
            <div className="grid md:grid-cols-3 gap-4 items-end" data-testid="availability-bulk-panel">
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Days of week</p>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAY_LABELS.map((label, i) => {
                    const on = bulkWeekdays.includes(i);
                    return (
                      <button
                        type="button"
                        key={label}
                        onClick={() => toggleWd(i)}
                        data-testid={`availability-weekday-${label.toLowerCase()}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-stone-200 text-stone-600 hover:border-amber-300"}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Months ahead</p>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  value={bulkMonths}
                  onChange={(e) => setBulkMonths(parseInt(e.target.value, 10) || 1)}
                  data-testid="availability-bulk-months"
                  disabled={!!bulkStart || !!bulkEnd}
                />
                <p className="text-[10px] text-stone-400 mt-1">Ignored when using a custom range below</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">From (optional)</p>
                <Input type="date" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} data-testid="availability-bulk-start" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Until (optional)</p>
                <Input type="date" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} data-testid="availability-bulk-end" />
              </div>
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Reason (label these blocks)</p>
                <Input
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="e.g. school"
                  data-testid="availability-bulk-reason"
                />
              </div>
              <Button
                onClick={bulkBlock}
                disabled={bulkSaving || bulkWeekdays.length === 0}
                className="h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-white gap-2"
                data-testid="availability-bulk-block-btn"
              >
                <CalendarX2 className="h-4 w-4" />
                {bulkSaving ? "Blocking…" : `Block matching days`}
              </Button>
              <div className="md:col-span-3 mt-2 pt-4 border-t border-stone-100">
                <p className="text-xs uppercase tracking-widest text-blue-800 mb-2">Unblock a date range</p>
                <p className="text-xs text-stone-500 mb-3">Frees every blocked date in the window. Handy for summer break.</p>
                <div className="grid md:grid-cols-3 gap-3 items-end">
                  <div>
                    <p className="text-xs text-stone-500 mb-1">From</p>
                    <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="availability-unblock-start" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 mb-1">Until</p>
                    <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="availability-unblock-end" />
                  </div>
                  <Button
                    onClick={unblockRange}
                    disabled={rangeSaving || !rangeStart || !rangeEnd}
                    className="h-11 rounded-full bg-blue-900 hover:bg-blue-800 text-white gap-2"
                    data-testid="availability-unblock-range-btn"
                  >
                    <CalendarCheck2 className="h-4 w-4" />
                    {rangeSaving ? "Freeing…" : "Unblock range"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {!bulkOpen && uniqueReasons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-xs text-stone-500 mr-1">Quick unblock:</span>
              {uniqueReasons.map((r) => (
                <button
                  key={r}
                  onClick={() => bulkUnblockByReason(r)}
                  data-testid={`availability-quick-unblock-${r}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"
                >
                  Unblock all &quot;{r}&quot; ({blocked.filter((b) => b.reason === r).length})
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-stone-100">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <CalendarOff className="h-5 w-5 text-amber-500" />
            <h3 className="font-serif-display text-2xl">Block a date</h3>
          </div>
          <p className="text-sm text-stone-500 mb-4">
            Vacation, sick day, or already committed elsewhere? Block dates so customers can&apos;t book them.
          </p>
          <div className="rounded-2xl border border-stone-200 p-2 bg-white inline-block">
            <Calendar
              mode="single"
              selected={pickedDate}
              onSelect={setPickedDate}
              disabled={disabledDay}
              modifiers={calendarModifiers}
              modifiersClassNames={calendarModifierClasses}
              components={calendarComponents}
            />
          </div>
          {(uniqueReasons.length > 0 || bookedDates.size > 0 || unlabeledDates.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {bookedDates.size > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-sky-100 text-blue-900">
                  <span className="inline-block h-2 w-2 rounded-full bg-blue-500" /> Booked
                </span>
              )}
              {uniqueReasons.map((r) => {
                const c = colorForReason(r);
                const count = blocked.filter((b) => b.reason === r).length;
                return (
                  <span key={r} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${c.soft}`}>
                    <span className={`inline-block h-2 w-2 rounded-full ${c.dot}`} /> {r} ({count})
                  </span>
                );
              })}
              {unlabeledDates.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-stone-100 text-stone-600">
                  <span className="inline-block h-2 w-2 rounded-full bg-stone-300" /> Unlabeled ({unlabeledDates.length})
                </span>
              )}
            </div>
          )}
          <div className="mt-4 space-y-3">
            <Input
              placeholder="Reason (optional) — e.g. Christmas Day"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="availability-reason-input"
            />
            <Button
              data-testid="availability-block-btn"
              onClick={addBlocked}
              disabled={!pickedDate || saving}
              className="w-full rounded-full h-11 bg-amber-500 hover:bg-amber-600 text-white gap-2"
            >
              <CalendarX2 className="h-4 w-4" />
              {pickedDate ? `Block ${format(pickedDate, "PPP")}` : "Pick a date to block"}
            </Button>
            <div className="flex items-center gap-3 pt-2 text-xs text-stone-500">
              <span>Legend appears below the calendar once you have blocked or booked dates.</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <Card className="border-stone-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h3 className="font-serif-display text-2xl">Blocked dates</h3>
              <Badge variant="outline" className="ml-auto rounded-full">{blocked.length}</Badge>
            </div>
            {blocked.length === 0 ? (
              <p className="text-sm text-stone-500">No manually blocked dates yet.</p>
            ) : (
              <ul className="space-y-2">
                {blocked.map((b) => {
                  const c = colorForReason(b.reason);
                  return (
                    <li key={b.date} data-testid="availability-blocked-row" className="flex items-center justify-between rounded-xl border border-stone-100 px-3 py-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`inline-block h-3 w-3 rounded-full shrink-0 ${c.dot}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-900">{format(new Date(b.date + "T00:00:00"), "EEE, MMM d, yyyy")}</p>
                          {b.reason && <p className="text-xs text-stone-500 truncate">{b.reason}</p>}
                        </div>
                      </div>
                      <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Unblock this date?</AlertDialogTitle>
                          <AlertDialogDescription>Customers will be able to book it again.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => unblock(b.date)} className="bg-amber-500 hover:bg-amber-600">Unblock</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarCheck2 className="h-5 w-5 text-blue-900" />
              <h3 className="font-serif-display text-2xl">Booked dates</h3>
              <Badge variant="outline" className="ml-auto rounded-full">{bookedDates.size}</Badge>
            </div>
            {bookedDates.size === 0 ? (
              <p className="text-sm text-stone-500">No bookings yet.</p>
            ) : (
              <ul className="space-y-1">
                {[...bookedDates].sort().map((d) => (
                  <li key={d} className="text-sm text-stone-700 flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                    {format(new Date(d + "T00:00:00"), "EEE, MMM d, yyyy")}
                    <span className="text-stone-400 text-xs ml-auto">auto-blocked</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
  );
}
